import { ThemeToggle } from './ThemeToggle';
import { formatTimeAgo } from '@/lib/utils';
import { useEffect, useRef, useState } from 'react';
import { cx } from 'class-variance-authority';
import { Globe2 } from 'lucide-react';
import { EditableToggle } from './EditableToggle';
import { PageActions } from './PageActions';
import ArchivedIndicator from './ArchivedIndicator';
import { SyncStatus } from './SyncStatus';
import { useCurrentPage } from '@/features/pages/hooks/useCurrentPage';
import { useCollectionStore } from '@/features/collections/stores/useCollectionStore';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';
import SearchButton from '@/features/pages/components/layout/Search';

const Menu = () => {
  const { data: currentPage } = useCurrentPage();
  const currentCollection = useCollectionStore(state => state.currentCollection);
  const [showTitle, setShowTitle] = useState(false);
  const lastScrollTop = useRef(0);
  const menuRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!menuRef.current) return;

      const scrollContainer = menuRef.current.closest('.overflow-y-auto');
      if (!scrollContainer) return;

      const scrollTop = scrollContainer.scrollTop;
      if (scrollTop > lastScrollTop.current && scrollTop > 60) {
        setShowTitle(true);
      } else if (scrollTop <= 60) {
        setShowTitle(false);
      }
      lastScrollTop.current = scrollTop <= 0 ? 0 : scrollTop;
    };

    const scrollContainer = menuRef.current?.closest('.overflow-y-auto');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  return (
    <nav ref={menuRef} className="sticky z-50 top-0 w-full bg-background">
      <div className="flex items-center justify-between p-2 pr-3 w-full">
        <div className="ml-10 flex gap-1 items-center">
          <div
            className={cx(
              'truncate text-md font-medium transition-opacity duration-300 ease-in-out',
              'max-w-[150px] sm:max-w-[220px] md:max-w-none overflow-hidden font-semibold',
              showTitle ? 'block' : 'hidden'
            )}
          >
            {currentPage?.title} {currentPage?.archivedAt ? '(Archived)' : ''}
          </div>
        </div>
        <div className="flex items-center gap-1 md:gap-2">
          <div className="block md:hidden">
            <SearchButton showTitle={false} />
          </div>
          {currentPage?.isPublic && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="px-3">
                    <Globe2 className="w-4 h-4 text-muted-foreground" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>These notes are currently public</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {currentPage && (
            <div className="hidden md:block text-muted-foreground text-sm mr-2 truncate">
              Edited {formatTimeAgo(currentPage.updatedAt)}
            </div>
          )}
          {!currentPage?.archivedAt && <EditableToggle />}
          <SyncStatus />
          <ThemeToggle />
          {currentPage && (
            <div className="h-[30px] w-[30px]">
              <PageActions disableActions={!!currentCollection?.archivedAt} />
            </div>
          )}
        </div>
      </div>
      <ArchivedIndicator disableActions={!!currentCollection?.archivedAt} />
    </nav>
  );
};

export default Menu;
